import type { PaginatedResponse } from '@/types/api';
import type {
    CompetencyProfile,
    CompetencyProfileGroup,
    CompetencyProfileItem,
} from '@/types/competency/CompetencyProfile';

import { api } from '@/api/api';

export interface CompetencyProfilePayload {
    name: Record<string, string>;
    description: Record<string, string>;
    sortOrder: number;
    items: CompetencyProfileItem[];
}

export interface CompetencyProfileDeletionImpact {
    groupsCount: number;
    competenciesCount: number;
    employeesCount: number;
}

export interface GetCompetencyProfilesParams {
    competencyId?: string;
    groupId?: string;
}

export default {
    async getCompetencyProfiles(
        params?: GetCompetencyProfilesParams,
    ): Promise<CompetencyProfile[]> {
        const response = await api.get(`/CompetencyProfile`, {
            params: {
                CompetencyId: params?.competencyId || undefined,
                GroupId: params?.groupId || undefined,
            },
        });

        return response.data;
    },

    async createCompetencyProfile(payload: CompetencyProfilePayload): Promise<CompetencyProfile> {
        const response = await api.post(`/CompetencyProfile`, payload);

        return response.data;
    },

    async updateCompetencyProfile(
        id: string,
        payload: CompetencyProfilePayload,
    ): Promise<CompetencyProfile> {
        const response = await api.put(`/CompetencyProfile/${id}`, payload);

        return response.data;
    },

    async deleteCompetencyProfile(id: string): Promise<CompetencyProfile> {
        const response = await api.delete(`/CompetencyProfile/${id}`);

        return response.data;
    },

    async getCompetencyProfileDeletionImpact(id: string): Promise<CompetencyProfileDeletionImpact> {
        const response = await api.get(`/CompetencyProfile/${id}/impact`);

        return response.data;
    },

    async removeCompetencyProfile(
        id: string,
        replacementProfileId: string | null,
    ): Promise<CompetencyProfile> {
        const response = await api.post(`/CompetencyProfile/${id}/Remove`, {
            replacementProfileId,
        });

        return response.data;
    },

    async setCompetencyProfileStatus(id: string, isActive: boolean): Promise<CompetencyProfile> {
        const response = await api.put(`/CompetencyProfile/${id}/status`, { isActive });

        return response.data;
    },

    async getCompetencyProfileWithGroups(
        id: string,
        params: { page: number; pageSize: number; employeeId?: string },
    ): Promise<PaginatedResponse<CompetencyProfileGroup>> {
        const response = await api.get(`/CompetencyProfile/${id}/groups`, { params });

        return response.data;
    },
};
